/**
 * 領収書台紙 API（/api/receipt-mounts）。F-EXP-05 / S-56・S-57
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { expenseKeys } from '../common/api';

export type MountCategory = '燃油代' | '潤滑油代' | '現像代';

export interface MountListItem {
  id: number;
  businessOperatorId: number;
  businessOperatorName: string;
  vesselId: number;
  vesselName: string;
  rosterNo: number;
  voyageReportId: number;
  periodFrom: string;
  periodTo: string;
  invoiceDate: string | null;
  fuelTotal: number;
  lubeTotal: number;
  devTotal: number;
  fuelRefNo: string | null;
  fuelPayee: string | null;
  outputFileName: string | null;
}

export interface MountLine {
  id: number;
  category: MountCategory;
  sortOrder: number;
  itemName: string;
  unitPrice: number | null;
  quantity: number | null;
  amount: number;
  note: string | null;
}

export interface MountDetail {
  id: number;
  fiscalYear: number;
  businessOperatorId: number;
  businessOperatorName: string;
  cooperativeName: string;
  vesselId: number;
  vesselName: string;
  rosterNo: number;
  voyageReportId: number;
  periodFrom: string;
  periodTo: string;
  criteriaFrom: string;
  criteriaTo: string;
  invoiceRegistrationNo: string | null;
  invoiceDate: string | null;
  claimantName: string | null;
  claimantAddress: string | null;
  fuelRefNo: string | null;
  fuelPayee: string | null;
  fuelDate: string | null;
  fuelContent: string | null;
  fuelUnitPrice: number | null;
  fuelQuantity: number | null;
  fuelAmountExclTax: number;
  fuelTax: number;
  fuelTotal: number;
  lubeRefNo: string | null;
  lubePayee: string | null;
  lubeDate: string | null;
  lubeContent: string | null;
  lubeUnitPrice: number | null;
  lubeQuantity: number | null;
  lubeAmountExclTax: number;
  lubeTotal: number;
  devRefNo: string | null;
  devPayee: string | null;
  devDate: string | null;
  devContent: string | null;
  devUnitPrice: number | null;
  devQuantity: number | null;
  devAmountExclTax: number;
  devTax: number;
  devTotal: number;
  outputFileName: string | null;
  lines: MountLine[];
}

export type MountUpdate = Partial<
  Pick<MountDetail, 'invoiceDate' | 'fuelRefNo' | 'fuelPayee' | 'fuelDate' | 'lubeRefNo' | 'lubePayee' | 'lubeDate' | 'lubeContent' | 'devRefNo' | 'devPayee' | 'devDate' | 'devContent'>
> & { lines?: { id: number; note: string | null }[] };

export const mountKeys = {
  all: [...expenseKeys.all, 'mounts'] as const,
  list: (year: number, f: Record<string, unknown>) => [...mountKeys.all, 'list', year, f] as const,
  detail: (id: number) => [...mountKeys.all, 'detail', id] as const,
};

export function useMounts(year: number, f: { businessOperatorId?: number | null; from?: string; to?: string; excludeVesselIds?: number[] }, enabled = true) {
  return useQuery({
    queryKey: mountKeys.list(year, f),
    queryFn: ({ signal }) => api.get<{ items: MountListItem[]; total: number }>('/receipt-mounts', { fiscalYear: year, ...f }, { signal }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useMount(id: number) {
  return useQuery({
    queryKey: mountKeys.detail(id),
    queryFn: ({ signal }) => api.get<MountDetail>(`/receipt-mounts/${id}`, undefined, { signal }),
  });
}

export function useGenerateMounts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { fiscalYear: number; businessOperatorId: number | null; from: string; to: string; excludeVesselIds: number[] }) =>
      api.post<{ ids: number[] }>('/receipt-mounts/generate', { ...input, businessOperatorId: input.businessOperatorId ?? undefined }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mountKeys.all }),
  });
}

export function useUpdateMount(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MountUpdate) => api.put<MountDetail>(`/receipt-mounts/${id}`, input),
    onSuccess: (d) => {
      qc.setQueryData(mountKeys.detail(id), d);
      return qc.invalidateQueries({ queryKey: mountKeys.all });
    },
  });
}

export function useDeleteMount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/receipt-mounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: mountKeys.all }),
  });
}
